import { downloadSalesReport } from '@/api/report-api';
import MyContainer from '@/components/MyContainer';
import { DateRangeFilter } from '@/components/report/date-range-filter';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronDown, FileSpreadsheet, FileText } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelReportTab } from './channel-report-tab';
import { CustomerReportTab } from './customer-report-tab';
import { PaymentReportTab } from './payment-report-tab';
import { ProductReportTab } from './product-report-tab';
import { getInitialDateRange } from './report-utils';
import { SaleReportTab } from './sale-report-tab';
import { TimeSeriesReportTab } from './time-series-report-tab';

export default function ReportPage() {
  const { t } = useTranslation('report');
  const [dateRange, setDateRange] = useState<{ fromDate: string; toDate: string }>(
    getInitialDateRange(),
  );
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: 'excel' | 'pdf') => {
    try {
      setIsExporting(true);
      await downloadSalesReport({
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
        format,
      });
      enqueueSnackbar(t('exportSuccess', { format: format.toUpperCase() }), {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error exporting report:', error);
      enqueueSnackbar(
        t('exportFailed', { error: error instanceof Error ? error.message : 'Unknown error' }),
        { variant: 'error' },
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <MyContainer
      enableBack={false}
      title={t('title')}
      subtitle={t('description')}
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button disabled={isExporting}>
              {isExporting ? t('exporting') : t('export')}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleExport('excel')}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              {t('exportAsExcel')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('pdf')}>
              <FileText className="mr-2 h-4 w-4" />
              {t('exportAsPdf')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <Tabs defaultValue="sale">
        <div className="mb-6 overflow-x-auto scrollbar-hide">
          <TabsList className="inline-flex w-auto flex-nowrap">
            <TabsTrigger value="sale" className="shrink-0 whitespace-nowrap">
              {t('categories.sale')}
            </TabsTrigger>
            <TabsTrigger value="product" className="shrink-0 whitespace-nowrap">
              {t('categories.product')}
            </TabsTrigger>
            <TabsTrigger value="customer" className="shrink-0 whitespace-nowrap">
              {t('categories.customer')}
            </TabsTrigger>
            <TabsTrigger value="channel" className="shrink-0 whitespace-nowrap">
              {t('categories.channel')}
            </TabsTrigger>
            <TabsTrigger value="timeseries" className="shrink-0 whitespace-nowrap">
              {t('categories.timeSeries')}
            </TabsTrigger>
            <TabsTrigger value="payment" className="shrink-0 whitespace-nowrap">
              {t('categories.payment')}
            </TabsTrigger>
          </TabsList>
        </div>

        <DateRangeFilter value={dateRange} onChange={setDateRange} className="mb-6" />
        <TabsContent value="sale">
          <SaleReportTab fromDate={dateRange.fromDate} toDate={dateRange.toDate} />
        </TabsContent>

        <TabsContent value="product" className="space-y-6">
          <ProductReportTab fromDate={dateRange.fromDate} toDate={dateRange.toDate} />
        </TabsContent>

        <TabsContent value="customer" className="space-y-6">
          <CustomerReportTab fromDate={dateRange.fromDate} toDate={dateRange.toDate} />
        </TabsContent>

        <TabsContent value="channel" className="space-y-6">
          <ChannelReportTab fromDate={dateRange.fromDate} toDate={dateRange.toDate} />
        </TabsContent>

        <TabsContent value="timeseries" className="space-y-6">
          <TimeSeriesReportTab fromDate={dateRange.fromDate} toDate={dateRange.toDate} />
        </TabsContent>

        <TabsContent value="payment" className="space-y-6">
          <PaymentReportTab fromDate={dateRange.fromDate} toDate={dateRange.toDate} />
        </TabsContent>
      </Tabs>
    </MyContainer>
  );
}
